import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { optionalEmailSchema } from '@/lib/validations'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { userService } from '@/services/user_service'
import { useAuth } from '@/hooks/useAuth'
import { useUserRoles } from '@/hooks/useUserRoles'
import { useMyPermissions } from '@/hooks/useMyPermissions'
import { useMe } from '@/hooks/useMe'
import { useViewMode } from '@/stores/viewMode'
import { AllowedAction } from '@/data/allowedActions'
import { PasswordResetSection } from '@/components/PasswordResetSection'
import { toast } from 'sonner'
import { AdSupplierUser } from '@magnetar/admin-api-interfaces'

const formSchema = z.object({
  account: z.string().min(1, { message: '請輸入使用者帳號' }),
  user_name: z.string().min(1, { message: '請輸入使用者名稱' }),
  email: optionalEmailSchema(),
  user_role_id: z.string().optional(),
})

type FormValues = z.infer<typeof formSchema>

interface EditAdSupplierUserDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  supplierUser: AdSupplierUser
  onSuccess?: () => void
}

export function EditAdSupplierUserDialog({
  open,
  onOpenChange,
  supplierUser,
  onSuccess,
}: EditAdSupplierUserDialogProps) {
  const { token } = useAuth()
  const { hasPermission } = useMyPermissions()
  const { user } = useMe()
  const { mode } = useViewMode()
  const hasEmail = !!supplierUser.user?.email
  const [pendingPassword, setPendingPassword] = useState<string | undefined>(undefined)

  // 階層權限檢查：sys_admin > provider (provider_supplier_management) > supplier (ad_supplier_user_management)
  const canResetPassword = (() => {
    if (user?.role_type === 'sys_admin') return true
    if (hasPermission('provider_supplier_management', AllowedAction.U)) return true
    if (mode === 'ad' && hasPermission('ad_supplier_user_management', AllowedAction.U)) return true
    return false
  })()

  // Fetch ad_supplier-level roles for this supplier
  const { userRoles, isLoading: isLoadingRoles } = useUserRoles({
    pageSize: 100,
    scopeType: 'ad_supplier',
    supplierId: supplierUser.supplier_id,
  })

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      account: supplierUser.user?.account || '',
      user_name: supplierUser.user?.user_name || '',
      email: supplierUser.user?.email || '',
      user_role_id: supplierUser.user_role_id?.toString() || '',
    },
  })

  useEffect(() => {
    if (open) {
      form.reset({
        account: supplierUser.user?.account || '',
        user_name: supplierUser.user?.user_name || '',
        email: supplierUser.user?.email || '',
        user_role_id: supplierUser.user_role_id?.toString() || '',
      })
      setPendingPassword(undefined)
    }
  }, [open, supplierUser, form])

  const onSubmit = async (values: FormValues) => {
    if (!token) return

    const data: {
      account: string
      user_name: string
      email?: string | null
      is_active: boolean
      user_role_id?: number
      password?: string
    } = {
      account: values.account,
      user_name: values.user_name,
      email: hasEmail ? supplierUser.user?.email : (values.email || null),
      is_active: true,
    }

    if (pendingPassword) {
      data.password = pendingPassword
    }

    if (values.user_role_id) {
      data.user_role_id = parseInt(values.user_role_id, 10)
    }

    const result = await userService.updateUser(supplierUser.user_id, data, token)

    if (result.success) {
      toast.success('更新使用者成功')
      onOpenChange(false)
      onSuccess?.()
    } else {
      toast.error(result.error?.message || '更新使用者失敗')
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>編輯供應商使用者</DialogTitle>
          <DialogDescription>編輯使用者基本資料</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="account"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>使用者帳號</FormLabel>
                  <FormControl>
                    <Input placeholder="請輸入使用者帳號" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="user_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>使用者名稱</FormLabel>
                  <FormControl>
                    <Input placeholder="請輸入使用者名稱" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder="example@domain.com"
                      disabled={hasEmail}
                      {...field}
                    />
                  </FormControl>
                  {hasEmail && (
                    <p className="text-xs text-muted-foreground">Email 已設定，無法修改</p>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="user_role_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>角色 (選填)</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={isLoadingRoles ? '載入中...' : '請選擇角色'} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {userRoles.map((role) => (
                        <SelectItem key={role.user_role_id} value={role.user_role_id.toString()}>
                          {role.role_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <PasswordResetSection
              canResetPassword={canResetPassword}
              onPasswordChange={setPendingPassword}
            />
            <DialogFooter>
              <Button type="submit">儲存</Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
